import { useEffect, useState } from 'react';
import logoImg from "../../assets/logo-cherry-color.webp";

interface LeaveReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (review: { rating: number; comment: string }) => void;
  proName: string;
}

export function LeaveReviewModal({ isOpen, onClose, onSubmit, proName }: LeaveReviewModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setRating(0);
      setComment('');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[420px] overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-8 max-h-[95vh] overflow-y-auto custom-scrollbar">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img src={logoImg} alt="Cherry Picked" className="h-[70px] object-contain" />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[20px] mb-2">
              Leave a review
            </h2>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px]">
              How was your experience with {proName}?
            </p>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-5" />

          {/* Rating Section */}
          <div className="mb-5">
            <label className="block font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-4">
              How would you rate the service?
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onMouseEnter={() => setRating(star)}
                  className="transition-transform active:scale-90"
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`${star <= rating ? 'text-[#fbbf24]' : 'text-[#ede8e9]'} transition-colors`}
                  >
                    <path
                      d="M11.0489 2.92705C11.3483 2.00574 12.6517 2.00574 12.9511 2.92705L14.4681 7.59599C14.6019 8.00793 14.9859 8.28691 15.419 8.28691H20.3279C21.2966 8.28691 21.6993 9.52628 20.9157 10.0955L16.9455 12.9799C16.5949 13.2346 16.4482 13.6861 16.582 14.0981L18.099 18.767C18.3984 19.6883 17.344 20.4544 16.5604 19.8851L12.5902 17.0007C12.2396 16.746 11.7604 16.746 11.4098 17.0007L7.43956 19.8851C6.65597 20.4544 5.60156 19.6883 5.901 18.767L7.41795 14.0981C7.55176 13.6861 7.4051 13.2346 7.05446 12.9799L3.08431 10.0955C2.30072 9.52628 2.70341 8.28691 3.67211 8.28691H8.58097C9.01413 8.28691 9.39812 8.00793 9.53193 7.59599L11.0489 2.92705Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-8" />

          {/* Experience Section */}
          <div className="mb-8">
            <label className="block font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-2">
              Tell others about your experience.
            </label>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[22px] mb-4">
              Was the professional punctual? Was the service done well? Would you book them again?
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write something..."
              className="w-full h-[140px] p-4 rounded-[12px] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[15px] text-[#3a3335] placeholder:text-[#908e8e] focus:outline-none focus:border-[#8e2a2f] resize-none transition-colors"
            />
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-8" />

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 h-[44px] bg-[#eee] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-sm hover:bg-[#e2e2e2] transition-all active:scale-[0.98] flex items-center justify-center"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit({ rating, comment })}
              disabled={rating === 0}
              className="flex-1 h-[44px] bg-[#fcfaf7] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-sm hover:bg-[#f2eee2] transition-all active:scale-[0.98] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
